import React from 'react';
import {
    Box,
    FormHelperText,
    IconButton,
    ListSubheader,
    MenuItem,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';

import DialogBigTextField from './DialogBigTextField';
import DialogWalletRuleField from './DialogWalletRuleField';
import Iconify from '../../components/iconify';
import i18n from '../../i18n';
import {
    DAY_RANGES,
    formatOrder,
    groupColumns,
    newKey,
    parseOrder,
    rangeOf,
    rangeValue,
    sameKey
} from '../../utils/walletOrder';

// The order's syntax note, for Text mode; the examples are the language itself, never translated.
const SYNTAX = [
    ['walletRules.orderSyntax.keys', '$count_active_wallet%d asc, $idle_seconds_wallet%d desc'],
    ['walletRules.orderSyntax.perWallet'],
    ['walletRules.syntax.sum', '$total_amount_per_day_wallet%d + $total_amount_active_wallet%d asc'],
    ['walletRules.syntax.failureSlot', '$count_continuous_wallet_2%d asc'],
    ['walletRules.syntax.dayRange', '$count_30_days_wallet[0+6]%d asc'],
    ['walletRules.syntax.whitespace']
];

/** A translation, or `fallback` when the key has none. */
const translated = (key, fallback, options) => {
    const text = i18n.t(key, options);
    return text && text !== key ? text : fallback;
};

const columnLabel = (name) => translated(`walletRules.order.columns.${name}`, `$${name}`);

const rangeLabel = (range) => {
    if (!range) return i18n.t('walletRules.order.days.all');
    const [start, count] = range;
    if (start === 0 && count === 0) return i18n.t('walletRules.order.days.today');
    if (start === 0) return i18n.t('walletRules.order.days.last', { n: count + 1 });
    return i18n.t('walletRules.order.days.range', { from: start, to: start + count });
};

/**
 * A wallet rule's orderRule: which of the wallets its filter admits core tries first.
 *
 * **Columns** (the default) is a list the operator builds from the variables the API
 * lists as `orderable`, for the rule's kind: each row a column picked from groups
 * (now, today, this month, the last 30 days, in a row, the wallet), its successes or
 * failures and its days where it has them, lowest or highest first, moved up and down;
 * wallets equal on every row go by id. It writes the order's text
 * (utils/walletOrder) on every change. **Text** is the BigText editor with the order's
 * syntax, for what the columns cannot show (a sum, a percentage) — such an order opens
 * there, saying why, and is never rewritten by the columns.
 *
 * The cabinet compiles the order on save and refuses one that does not compile
 * (422 `WALLET_RULE_INVALID: Order: …`); the dialog shows that as any other refusal.
 */
export default class DialogWalletOrderField extends DialogWalletRuleField {
    constructor(props) {
        super(props);
        this.state = { ...this.state, mode: parseOrder(props.value) ? 'columns' : 'text' };
    }

    syntax() {
        return SYNTAX;
    }

    listedVariables(variables) {
        return variables.filter((v) => v.orderable);
    }

    /** The columns an order of this rule's kind may sort by; every kind's until one is chosen. */
    columns() {
        const { variables } = this.state;
        if (!Array.isArray(variables)) return [];
        const kind = this.props.data?.kind;
        return variables.filter((v) => v.orderable && (!kind || (v.kinds || []).includes(kind)));
    }

    disabled() {
        return Boolean(this.props.field.onlyRead || this.props.disabled);
    }

    emit(keys) {
        this.props.onChange(this.props.field.id, formatOrder(keys));
    }

    handleModeChange = (event, mode) => {
        if (!mode || (mode === 'columns' && parseOrder(this.props.value) === null)) return;
        this.setState({ mode });
    };

    /** The grouped MenuItems of the columns, plus `current` when it is not among them. */
    columnItems(current) {
        const columns = this.columns();
        const items = [];
        groupColumns(columns).forEach(({ group, variables }) => {
            items.push(
                <ListSubheader key={`group-${group}`}>{i18n.t(`walletRules.order.groups.${group}`)}</ListSubheader>
            );
            variables.forEach((v) =>
                items.push(
                    <MenuItem key={v.name} value={v.name} sx={{ display: 'block', maxWidth: 480 }}>
                        <Typography variant="body2">{columnLabel(v.name)}</Typography>
                        {v.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                {v.description}
                            </Typography>
                        )}
                    </MenuItem>
                )
            );
        });
        if (current && !columns.some((v) => v.name === current)) {
            items.push(
                <MenuItem key={current} value={current}>
                    {`$${current}`}
                </MenuItem>
            );
        }
        return items;
    }

    renderKey(key, index, keys) {
        const disabled = this.disabled();
        const variable = this.columns().find((v) => v.name === key.name);
        const duplicate = keys.slice(0, index).some((earlier) => sameKey(earlier, key));
        const boolean = key.type === '%b';
        const change = (changes) => this.emit(keys.map((k, i) => (i === index ? { ...k, ...changes } : k)));
        const move = (to) => {
            const moved = [...keys];
            [moved[index], moved[to]] = [moved[to], moved[index]];
            this.emit(moved);
        };
        const ranges = DAY_RANGES.some((r) => rangeValue(r) === rangeValue(key.range))
            ? DAY_RANGES
            : [...DAY_RANGES, key.range];

        return (
            <Box
                key={index}
                data-testid="order-key"
                sx={{ p: 1, border: 1, borderColor: duplicate ? 'warning.main' : 'divider', borderRadius: 1 }}
            >
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="subtitle2" sx={{ minWidth: 20 }}>{`${index + 1}.`}</Typography>
                    <TextField
                        select
                        size="small"
                        label={i18n.t('walletRules.order.column')}
                        value={key.name}
                        disabled={disabled}
                        sx={{ minWidth: 220, flex: 1 }}
                        SelectProps={{ renderValue: columnLabel }}
                        onChange={(event) => {
                            const picked = this.columns().find((v) => v.name === event.target.value);
                            if (picked) change(newKey(picked));
                        }}
                    >
                        {this.columnItems(key.name)}
                    </TextField>
                    {variable?.failureSlot && (
                        <TextField
                            select
                            size="small"
                            value={key.failed ? 'failed' : 'successful'}
                            disabled={disabled}
                            onChange={(event) => change({ failed: event.target.value === 'failed' })}
                        >
                            <MenuItem value="successful">{i18n.t('walletRules.order.successful')}</MenuItem>
                            <MenuItem value="failed">{i18n.t('walletRules.order.failed')}</MenuItem>
                        </TextField>
                    )}
                    {variable?.dayRange && (
                        <TextField
                            select
                            size="small"
                            value={rangeValue(key.range)}
                            disabled={disabled}
                            onChange={(event) => change({ range: rangeOf(event.target.value) })}
                        >
                            {ranges.map((range) => (
                                <MenuItem key={rangeValue(range)} value={rangeValue(range)}>
                                    {rangeLabel(range)}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={key.descending ? 'desc' : 'asc'}
                        disabled={disabled}
                        onChange={(event, direction) => direction && change({ descending: direction === 'desc' })}
                    >
                        <ToggleButton value="asc">
                            {i18n.t(boolean ? 'walletRules.order.ascBoolean' : 'walletRules.order.asc')}
                        </ToggleButton>
                        <ToggleButton value="desc">
                            {i18n.t(boolean ? 'walletRules.order.descBoolean' : 'walletRules.order.desc')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Stack direction="row">
                        {[
                            ['up', 'eva:arrow-upward-fill', index === 0, () => move(index - 1)],
                            ['down', 'eva:arrow-downward-fill', index === keys.length - 1, () => move(index + 1)],
                            ['remove', 'eva:trash-2-outline', false, () => this.emit(keys.filter((k, i) => i !== index))]
                        ].map(([action, icon, off, onClick]) => (
                            <Tooltip key={action} title={i18n.t(`walletRules.order.${action}`)}>
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label={i18n.t(`walletRules.order.${action}`)}
                                        disabled={disabled || off}
                                        onClick={onClick}
                                    >
                                        <Iconify icon={icon} width={18} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ))}
                    </Stack>
                </Stack>
                {variable?.description && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                        {variable.description}
                    </Typography>
                )}
                {duplicate && (
                    <Typography variant="caption" color="warning.main" component="div">
                        {i18n.t('walletRules.order.duplicate')}
                    </Typography>
                )}
            </Box>
        );
    }

    renderColumns(keys) {
        const { variables } = this.state;
        const columns = this.columns();
        return (
            <Stack spacing={1}>
                {keys.map((key, index) => this.renderKey(key, index, keys))}
                <Typography variant="caption" color="text.secondary">
                    {i18n.t(keys.length ? 'walletRules.order.thenById' : 'walletRules.order.idOrder')}
                </Typography>
                {variables === false ? (
                    <Typography variant="body2" color="error">
                        {i18n.t('walletRules.variablesUnavailable')}
                    </Typography>
                ) : (
                    <TextField
                        select
                        size="small"
                        label={i18n.t('walletRules.order.add')}
                        value=""
                        disabled={this.disabled() || columns.length === 0}
                        sx={{ maxWidth: 320 }}
                        onChange={(event) => {
                            const picked = columns.find((v) => v.name === event.target.value);
                            if (picked) this.emit([...keys, newKey(picked)]);
                        }}
                    >
                        {this.columnItems(null)}
                    </TextField>
                )}
                {keys.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {formatOrder(keys)}
                    </Typography>
                )}
            </Stack>
        );
    }

    render() {
        const { field, error, value } = this.props;
        const keys = parseOrder(value);
        const mode = keys === null ? 'text' : this.state.mode;
        const helper = error || (field.helperText ? i18n.t(`helperTexts.${field.helperText}`) : '');

        return (
            <Box>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color={error ? 'error' : 'text.primary'}>
                        {i18n.t(`columns.${field.label}`)}
                    </Typography>
                    <ToggleButtonGroup size="small" exclusive value={mode} onChange={this.handleModeChange}>
                        <ToggleButton value="columns" disabled={keys === null}>
                            {i18n.t('walletRules.order.modeColumns')}
                        </ToggleButton>
                        <ToggleButton value="text">{i18n.t('walletRules.order.modeText')}</ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
                {mode === 'columns' ? (
                    <>
                        {this.renderColumns(keys)}
                        {helper && <FormHelperText error={Boolean(error)}>{helper}</FormHelperText>}
                    </>
                ) : (
                    <Stack spacing={1}>
                        {keys === null && (
                            <Typography variant="caption" color="text.secondary">
                                {i18n.t('walletRules.order.textOnly')}
                            </Typography>
                        )}
                        <DialogBigTextField {...this.props} />
                        {this.renderHelp()}
                    </Stack>
                )}
            </Box>
        );
    }
}
